import React, { useRef, useState, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import RenderLabel from "./Renderer/RenderLabel";
import RenderWidgets from "./Renderer/RenderWigets";
import GoogleSignInButton from "../../common/ui/GoogleSignInButton";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { GOOGLE_CLIENT_ID } from "../../utils/constant/globalEnv.constant";
import { googleLoginOrRegister, register } from "../../services/public.services";
import { toastMessage } from "../../utils/helper/myToast";
import { extractDeviceInfo } from "../../utils/helper/browser";
import { useNavigate } from "react-router-dom";
import { setSignupInfo, setValidateMfaDetails } from "../../store/features/loginSlice";
import Button from "@/common/ui/Button";
import ReCaptchaInput, { ReCaptchaHandle } from "@/common/input/ReCaptchaInput";
import { loginSuccessAction } from "@/store/actions/login.actions";
import { useAppDispatch } from "@/store/store";
import CustomLink from "@/common/ui/CustomLink";
import Secure from "@/secure/payload";
import { fetchUserLocation } from "../../store/features/globalSlice";

interface SignUpFormProps {
  SignUpFormConfig: any;
  signupType: string;
}

const SignUpForm: React.FC<SignUpFormProps> = ({ SignUpFormConfig, signupType }) => {
  const methods = useForm({ mode: "onBlur", reValidateMode: "onChange" });
  const [isGoogleLogin, setIsGoogleLogin] = useState(false);
  const [detectedRegion, setDetectedRegion] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const reCaptchaRef = useRef<ReCaptchaHandle>(null);

  // Detect user's region on component mount
  useEffect(() => {
    const detectRegion = async () => {
      try {
        const result = await dispatch(fetchUserLocation());
        if (fetchUserLocation.fulfilled.match(result)) {
          // Use country_code as region, or fallback to country name
          const region = result.payload?.country_code || result.payload?.country || null;
          setDetectedRegion(region);
        }
      } catch (error) {
        console.error("Failed to detect region:", error);
        // Fallback: try to get from localStorage if available
        const storedRegion = localStorage.getItem("data-region");
        if (storedRegion) {
          setDetectedRegion(storedRegion);
        }
      }
    };
    detectRegion();
  }, [dispatch]);

  const {
    handleSubmit,
    // watch,
    formState: { isSubmitting },
  } = methods;
  // console.log({ errors });
  // console.log(watch());

  const handleMFA = (data: any) => {
    console.log(data);
    dispatch(setValidateMfaDetails({ token: data.mfaToken, mfaType: data.mfaType, identity: data?.identity }));
    navigate("/validate-mfa");
  };

  const handleDirectSignUp = async (data: any) => {
    let recaptcha = null;

    if (reCaptchaRef.current) {
      recaptcha = await reCaptchaRef.current.getRecaptchaToken();
    }
    const { agreeTerms, confirmPassword, email, businessEmail, ...rest } = data;    
    // Use detected region, or fallback to stored region, or null if not available
    const region = detectedRegion || localStorage.getItem("data-region") || null;
    
    const res: any = await register({ ...rest, recaptcha, signupType, email: email ?? businessEmail, isTncAccepted: true, dataRegion : region });

    // console.log({ res });

    if (res?.status === 201) {
      const { message, isError, showMessage } = res.data;
      dispatch(setSignupInfo(Secure.payloadDecryption({ ...res.data?.data })));
      navigate(`/verify-account`);
      if (showMessage) return toastMessage(message, isError);
    } else {
      console.log("Error", res);
      const { message, isError } = res?.response?.data || {};
      if (message) {
        return toastMessage(message, isError);
      }
    }
  };

  const handleGoogleSignUp = async (data: any) => {
    try {
      const browserInfo = await extractDeviceInfo();
      const payload: any = { signupType, browserInfo: { ...browserInfo } };
      // OneTab
      if (data?.credential) {
        console.log("One Tab Google SignUP");
        payload.credentialToken = data?.credential;
      } else if (data?.code) {
        setIsGoogleLogin(true);
        console.log("Google Sign-up");
        payload.code = data?.code;
      }
      const res: any = await googleLoginOrRegister(payload);
      if (res?.status === 200) {
        const { message, isError, showMessage, data } = res.data;
        // console.log("GOOGLE_SIGNUP_RESULT", data);
        dispatch(loginSuccessAction(data));
        navigate("/dashboard");
        if (showMessage) return toastMessage(message, isError);
      } else if (res?.status === 302) {
        // console.log("This is Response", { res });
        const { data } = res?.response?.data || {};
        handleMFA(data);
      } else {
        console.log("Error", res);
        const { message, isError } = res?.response?.data || {};
        if (message) {
          return toastMessage(message, isError);
        }
      }
    } catch (err) {
      console.log({ err });
    } finally {
      setIsGoogleLogin(false);
    }
  };

  return (
    <div className="pb-20 px-4 md:px-0">
      <div className={` p-1 md:p-4 shadow-form rounded-llg`}>
        <h2 className="font-bold text-2xl text-center text-text-label">{SignUpFormConfig.formName}</h2>
        <FormProvider {...methods}>
          <form className="h-fit items-center md:items-start flex flex-col w-full gap-y-3 py-6" onSubmit={handleSubmit(handleDirectSignUp)}>
            {SignUpFormConfig.sections.map((section: any) => {
              return section.widgets?.map((widget: any) => {
                return (
                  <div className={`flex ${widget?.className ? widget?.className : "flex-col"} w-full gap-1  `} key={widget?.field}>
                    {SignUpFormConfig?.showLabel && !widget?.hideLabel && (
                      <RenderLabel label={widget.label} required={widget?.validation?.required} labelInfo={widget?.labelInfo} className=" text-nowrap " />
                    )}
                    <div className="">
                      <RenderWidgets widget={widget} />
                    </div>
                  </div>
                );
              });
            })}
            {/* submit Button */}
            <Button variant="primary" size="small" isFullWidth disabled={isSubmitting} isLoading={isSubmitting}>
              Sign up
            </Button>
          </form>
        </FormProvider>

        {/* OR */}
        <div className="flex items-center mb-4">
          <div className="flex-grow border-t border-gray-300"></div>
          <span className="flex-shrink mx-6 text-gray-600">or</span>
          <div className="flex-grow border-t border-gray-300"></div>
        </div>
        {/* Google Sign-in */}
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
          <GoogleSignInButton handleSignIn={handleGoogleSignUp} btnText="Sign up with Google" isLoading={isGoogleLogin} />
        </GoogleOAuthProvider>
        {/* google tnc */}
        <p className="mt-1.5 text-center text-sm text-gray-500">
          By signing up with Google, you agree to our <br />
          <CustomLink
            className="text-13 font-normal leading-normal hover:underline"
            isExternal
            link={{ value: "https://www.lystproof.com/terms-conditions", text: "Terms & Conditions" }}
          />
          and
          <CustomLink
            className="text-13 font-normal leading-normal hover:underline"
            isExternal
            link={{ value: "https://www.lystproof.com/privacy", text: "Privacy Policy" }}
          />
        </p>
        {/* Invisible ReCaptcha */}
        <ReCaptchaInput ref={reCaptchaRef} />
      </div>
    </div>
  );
};

export default SignUpForm;
